import React from 'react';
import { logError, exportErrorLog } from './errorLog';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      errorMessage: error instanceof Error ? error.message : String(error),
    };
  }

  override componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    void logError({
      level: 'critical',
      location: 'App',
      message: error instanceof Error ? error.message : String(error),
      stack: info.componentStack ?? undefined,
    });
  }

  private handleLogout = (): void => {
    // Session-Key wipe + Reload
    import('../../services/keyStore').then(({ SessionKeyStore }) => {
      SessionKeyStore.wipe();
      window.location.reload();
    }).catch(() => window.location.reload());
  };

  private handleClearCache = (): void => {
    if (!window.confirm('Lokalen Cache leeren? Alle ungesicherten Daten gehen verloren!')) return;
    indexedDB.deleteDatabase('AmedDB');
    window.location.reload();
  };

  private handleExportLog = async (): Promise<void> => {
    try {
      const json = await exportErrorLog();
      const blob = new Blob([json], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `amed-error-log-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Fehler-Log konnte nicht exportiert werden.');
    }
  };

  override render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-8">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-red-200 shadow-lg p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-red-600 text-xl">⚠</span>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Kritischer Anwendungsfehler</h1>
              <p className="text-sm text-gray-500">Die App konnte nicht fortfahren.</p>
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-4 border border-red-100">
            <p className="text-sm font-mono text-red-800 break-all">
              {this.state.errorMessage}
            </p>
          </div>

          <p className="text-sm text-gray-600">
            Aus Datensicherheitsgründen ist kein automatisches Recovery möglich.
            Bitte wählen Sie eine der folgenden Optionen:
          </p>

          <div className="space-y-3">
            <button onClick={this.handleLogout} className="btn-primary w-full justify-center">
              Neu einloggen (empfohlen)
            </button>
            <button
              onClick={() => { void this.handleExportLog(); }}
              className="btn-secondary w-full justify-center"
            >
              Fehler-Log für Bug-Report exportieren
            </button>
            <button
              onClick={this.handleClearCache}
              className="w-full px-4 py-2 rounded-lg border border-red-200 text-red-700 text-sm
                         font-medium hover:bg-red-50 transition-colors"
            >
              Cache leeren (Datenverlust möglich!)
            </button>
          </div>
        </div>
      </div>
    );
  }
}
